import React, {useEffect, useMemo, useState} from "react";
import {FiCheck, FiDownload, FiEdit2, FiFileText, FiPlus, FiSearch, FiTag, FiTrash2, FiX,} from "react-icons/fi";
import {PiBuildings} from "react-icons/pi";
import {getProductions, getTags, createProduction, exportProduction, downloadProduction, deleteProduction} from "../../../api";
import {
    BateStampLocation,
    JobType,
    OrganizationFilter,
    ProductionCreate,
    ProductionRead,
    TagRead,
    TagType,
} from "../../../schemas";
import {requiresAssociationConsistency, isTranscriptExchangeTag} from "../../../schemas/tag";
import {ENABLE_ENTITY_TAGS} from "../../../feature_flags";
import {withApiErrorHandler} from "../../../error_handling";
import {useNavigationContext} from "../../../contexts/NavContext/nav-context";
import {useGroupContext} from "../../../contexts/group_context";
import {useJobContext} from "../../../contexts/job_context";
import Swal from "sweetalert2";
import JobProgressList from "../../Jobs";
import {ButtonColors, TextColors, ThemeColors, ThemeColorsBorder} from "../../../colors";
import {Loader} from "../../Loader";
import {PRODUCTION_COMPLETED} from "../../../events";

const BATE_STAMP_LABELS: Record<BateStampLocation, string> = {
    [BateStampLocation.TOP_LEFT]: "Top Left",
    [BateStampLocation.TOP_CENTER]: "Top Center",
    [BateStampLocation.TOP_RIGHT]: "Top Right",
    [BateStampLocation.BOTTOM_LEFT]: "Bottom Left",
    [BateStampLocation.BOTTOM_CENTER]: "Bottom Center",
    [BateStampLocation.BOTTOM_RIGHT]: "Bottom Right",
};

/** Collapsible tag section with checkbox list */
const TagSection: React.FC<{
    title: string;
    icon: React.ReactNode;
    tags: TagRead[];
    selectedTags: string[];
    toggleTag: (id: string) => void;
}> = ({title, icon, tags, selectedTags, toggleTag}) => {
    const [expanded, setExpanded] = useState(true);
    const selectedCount = tags.filter(t => selectedTags.includes(t.id)).length;

    if (tags.length === 0) return null;

    return (
        <div className="border border-zinc-200 rounded-lg overflow-hidden bg-white">
            <button
                type="button"
                onClick={() => setExpanded(e => !e)}
                className="flex items-center gap-2 w-full px-3 py-2 bg-zinc-50 border-b border-zinc-200 hover:bg-zinc-100 transition-colors"
            >
                <span className="text-zinc-500">{icon}</span>
                <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{title}</span>
                {selectedCount > 0 && (
                    <span className="inline-flex items-center justify-center rounded-full bg-zinc-500 px-1.5 py-0.5 text-[10px] font-medium text-white">
                        {selectedCount}
                    </span>
                )}
                <span className="ml-auto text-[10px] text-zinc-400">{tags.length}</span>
                <span className={`text-zinc-400 transition-transform ${expanded ? 'rotate-180' : ''}`}>
                    <svg width="12" height="12" viewBox="0 0 12 12"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>
                </span>
            </button>
            {expanded && (
                <div className="max-h-48 overflow-y-auto p-1.5">
                    {tags.map(tag => {
                        const isSelected = selectedTags.includes(tag.id);
                        return (
                            <label
                                key={tag.id}
                                className={`flex items-center gap-2.5 cursor-pointer p-2 rounded-md transition-colors ${
                                    isSelected ? 'bg-zinc-100' : 'hover:bg-zinc-50'
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 text-zinc-600 border-zinc-300 rounded focus:ring-zinc-400"
                                    checked={isSelected}
                                    onChange={() => toggleTag(tag.id)}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-zinc-800 truncate">{tag.tag_name}</span>
                                        {tag.document_count > 0 && (
                                            <span className="text-[10px] text-zinc-400 tabular-nums shrink-0">{tag.document_count}</span>
                                        )}
                                    </div>
                                    {tag.description && (
                                        <div className="text-xs text-zinc-500 truncate">{tag.description}</div>
                                    )}
                                </div>
                            </label>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const PaletteProduction: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [productions, setProductions] = useState<ProductionRead[]>([]);
    const [tags, setTags] = useState<TagRead[]>([]);
    const [showCreateForm, setShowCreateForm] = useState(false);

    // form state
    const [productionName, setProductionName] = useState("");
    const [productionDescription, setProductionDescription] = useState("");
    const [bateStampLocation, setBateStampLocation] =
        useState<BateStampLocation>(BateStampLocation.BOTTOM_RIGHT);
    const [bateInFilename, setBateInFilename] = useState(true);
    const [bateInPageContent, setBateInPageContent] = useState(false);
    const [batePrefix, setBatePrefix] = useState("PROD");
    const [organizationFilter, setOrganizationFilter] =
        useState<OrganizationFilter>(OrganizationFilter.RELEVANT);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [tagSearch, setTagSearch] = useState('');

    // Organize tags by type
    const tagGroups = useMemo(() => {
        const q = tagSearch.toLowerCase().trim();
        const filterAndSort = (list: TagRead[]) => {
            const filtered = q
                ? list.filter(t => t.tag_name.toLowerCase().includes(q) || (t.description?.toLowerCase().includes(q)))
                : list;
            return filtered.sort((a, b) => {
                const aS = selectedTags.includes(a.id);
                const bS = selectedTags.includes(b.id);
                if (aS && !bS) return -1;
                if (!aS && bS) return 1;
                return a.tag_name.localeCompare(b.tag_name);
            });
        };
        return {
            codes: filterAndSort(tags.filter(t => requiresAssociationConsistency(t) && t.type !== TagType.REQUEST_SET)),
            requests: filterAndSort(tags.filter(t => t.type === TagType.REQUEST_SET)),
            transcript: filterAndSort(tags.filter(t => isTranscriptExchangeTag(t))),
            entities: filterAndSort(tags.filter(t => t.type === TagType.ENTITY)),
        };
    }, [tags, tagSearch, selectedTags]);

    const {navigateTo} = useNavigationContext()!;
    const {currentGroup} = useGroupContext()!;
    const {activeJobs, addJobs} = useJobContext()!;

    /* ------------------------------------------------------------------ */
    /* initial load                                                        */
    /* ------------------------------------------------------------------ */

    useEffect(() => {
        if (!currentGroup) return;
        loadProductions();
        loadTags();
    }, [currentGroup]);

    /* ------------------------------------------------------------------ */
    /* production completed listener                                       */
    /* ------------------------------------------------------------------ */

    useEffect(() => {
        if (!currentGroup) return;

        const onProductionCompleted = (event: Event) => {
            const customEvent = event as CustomEvent<{
                production_id?: string;
                group_id?: string;
            }>;

            if (
                customEvent.detail?.group_id &&
                customEvent.detail.group_id !== currentGroup.id
            ) {
                return;
            }

            loadProductions();

            Swal.fire({
                icon: "success",
                title: "Production Ready",
                text: "Your production has finished processing and is ready to download.",
                timer: 3000,
                showConfirmButton: false,
            });
        };

        window.addEventListener(
            PRODUCTION_COMPLETED,
            onProductionCompleted
        );
        return () =>
            window.removeEventListener(
                PRODUCTION_COMPLETED,
                onProductionCompleted
            );
    }, [currentGroup]);

    /* ------------------------------------------------------------------ */
    /* derived data                                                        */
    /* ------------------------------------------------------------------ */

    // Map job_id to job for quick lookup
    const jobsByIdMap = new Map(
        activeJobs
            .filter((job) => job.type === JobType.PRODUCTION && job.group_id === currentGroup?.id)
            .map((job) => [job.id, job])
    );

    // Helper to get active job for a production
    const getActiveJobForProduction = (production: ProductionRead) => {
        if (!production.job_id) return null;
        return jobsByIdMap.get(production.job_id) ?? null;
    };

    /* ------------------------------------------------------------------ */
    /* loaders                                                             */
    /* ------------------------------------------------------------------ */

    const loadProductions = async () => {
        if (!currentGroup) return;

        try {
            const prods =
                (await withApiErrorHandler(
                    () => getProductions(currentGroup.id),
                    {
                        titleQualifier: "Error Loading Productions",
                        navigateTo,
                    }
                )) || [];

            setProductions(prods);
        } finally {
            setInitialLoading(false);
        }
    };

    const loadTags = async () => {
        const allTags =
            (await withApiErrorHandler(
                () => getTags(currentGroup?.id),
                {
                    titleQualifier: "Error Loading Tags",
                    navigateTo,
                }
            )) || [];

        setTags(allTags);
    };

    /* ------------------------------------------------------------------ */
    /* actions                                                             */
    /* ------------------------------------------------------------------ */

    const handleCreateProduction = async (
        event: React.FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();
        if (!currentGroup) return;

        if (!productionName.trim()) {
            await Swal.fire(
                "Missing Name",
                "Please enter a production name",
                "warning"
            );
            return;
        }

        if (selectedTags.length === 0) {
            await Swal.fire(
                "No Tags Selected",
                "Please select at least one tag to include in this production",
                "warning"
            );
            return;
        }

        setLoading(true);

        try {
            const payload: ProductionCreate = {
                name: productionName,
                description: productionDescription,
                group_id: currentGroup.id,
                bate_stamp_location: bateStampLocation,
                bate_in_filename: bateInFilename,
                bate_in_page_content: bateInPageContent,
                bate_prefix: batePrefix,
                organization_filter: organizationFilter,
                tag_ids: selectedTags,
            };

            await withApiErrorHandler(
                () => createProduction(payload),
                {titleQualifier: "Error Creating Production", navigateTo}
            );

            await Swal.fire({
                icon: "success",
                title: "Production Created",
                confirmButtonColor: "#3a3a4a",
            });

            setShowCreateForm(false);
            setProductionName("");
            setProductionDescription("");
            setSelectedTags([]);
            await loadProductions();
        } finally {
            setLoading(false);
        }
    };

    const handleExportProduction = async (productionId: string) => {
        setLoading(true);
        try {
            const jobRead = await withApiErrorHandler(
                () => exportProduction(productionId),
                {titleQualifier: "Error Starting Export", navigateTo}
            );
            if (jobRead) addJobs([jobRead]);

            await Swal.fire({
                icon: "info",
                title: "Export Started",
                text: "You can download it once processing is complete.",
                confirmButtonColor: "#3a3a4a",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadProduction = async (productionId: string) => {
        setLoading(true);
        try {
            const result = await withApiErrorHandler(
                () => downloadProduction(productionId),
                {titleQualifier: "Error Downloading Production", navigateTo}
            );

            if (result?.download_url) {
                window.open(result.download_url, "_blank");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteProduction = async (
        productionId: string,
        name: string
    ) => {
        const result = await Swal.fire({
            title: "Delete Production?",
            text: `Delete "${name}"? This cannot be undone.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
        });

        if (!result.isConfirmed) return;

        setLoading(true);
        try {
            await withApiErrorHandler(
                () => deleteProduction(productionId),
                {titleQualifier: "Error Deleting Production", navigateTo}
            );
            await loadProductions();
        } finally {
            setLoading(false);
        }
    };

    const toggleTag = (id: string) => {
        setSelectedTags((prev) =>
            prev.includes(id)
                ? prev.filter((x) => x !== id)
                : [...prev, id]
        );
    };

    /* ------------------------------------------------------------------ */
    /* render                                                              */
    /* ------------------------------------------------------------------ */

    return (
        <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Productions</h3>
                <button
                    onClick={() => setShowCreateForm((v) => !v)}
                    className={`flex items-center gap-2 px-3 py-2 ${ButtonColors.dark} ${ButtonColors.hoverDark} text-white rounded-lg`}
                >
                    {showCreateForm ? <FiX/> : <FiPlus/>}
                    {showCreateForm ? "Cancel" : "New"}
                </button>
            </div>

            {/* Create Form */}
            {showCreateForm && (
                <form onSubmit={handleCreateProduction}
                      className={`border ${ThemeColorsBorder.neutralMid} rounded-lg p-4 ${ThemeColors.neutralLight} space-y-4`}>
                    <div>
                        <label className={`block text-sm font-medium ${TextColors.mid} mb-1`}>
                            Production Name *
                        </label>
                        <input
                            type="text"
                            className={`w-full border ${ThemeColorsBorder.neutralMid} rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-zinc-400 focus:border-zinc-400`}
                            value={productionName}
                            onChange={(e) => setProductionName(e.target.value)}
                            placeholder="e.g., Q1 2024 Production"
                            required
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-medium ${TextColors.mid} mb-1`}>
                            Description
                        </label>
                        <textarea
                            className={`w-full border ${ThemeColorsBorder.neutralMid} rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-zinc-400 focus:border-zinc-400 resize-vertical`}
                            value={productionDescription}
                            onChange={(e) => setProductionDescription(e.target.value)}
                            placeholder="Optional description..."
                            rows={2}
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-medium ${TextColors.mid} mb-1`}>
                            Bate Prefix
                        </label>
                        <select
                            className={`w-full border ${ThemeColorsBorder.neutralMid} rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-zinc-400 focus:border-zinc-400`}
                            value={batePrefix}
                            onChange={(e) => setBatePrefix(e.target.value)}
                        >
                            <option value="PROD">PROD (Production)</option>
                            <option value="DOC">DOC (Document)</option>
                            <option value="EXHIBIT">EXHIBIT (Legal Exhibits)</option>
                            <option value="PLAINTIFF">PLAINTIFF (Litigation)</option>
                            <option value="DEFENDANT">DEFENDANT (Litigation)</option>
                        </select>
                    </div>

                    <div>
                        <label className={`block text-sm font-medium ${TextColors.mid} mb-2`}>
                            Bate Stamp Options
                        </label>
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="bateInFilename"
                                    className={`w-4 h-4 text-zinc-600 ${ThemeColorsBorder.neutralMid} rounded focus:ring-zinc-400`}
                                    checked={bateInFilename}
                                    onChange={(e) => setBateInFilename(e.target.checked)}
                                />
                                <label htmlFor="bateInFilename" className={`text-sm ${TextColors.mid}`}>
                                    Include bate stamp in filename
                                </label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="bateInPageContent"
                                    className={`w-4 h-4 text-zinc-600 ${ThemeColorsBorder.neutralMid} rounded focus:ring-zinc-400`}
                                    checked={bateInPageContent}
                                    onChange={(e) => setBateInPageContent(e.target.checked)}
                                />
                                <label htmlFor="bateInPageContent" className={`text-sm ${TextColors.mid}`}>
                                    Embed bate stamp in PDF page content
                                </label>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className={`block text-sm font-medium ${TextColors.mid} mb-1`}>
                            Bate Stamp Location (AI will avoid laying over text)
                        </label>
                        <select
                            className={`w-full border ${ThemeColorsBorder.neutralMid} rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-zinc-400 focus:border-zinc-400`}
                            value={bateStampLocation}
                            onChange={(e) => setBateStampLocation(e.target.value as BateStampLocation)}
                        >
                            {Object.entries(BATE_STAMP_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className={`block text-sm font-medium ${TextColors.mid} mb-1`}>
                            Document Organization
                        </label>
                        <select
                            className={`w-full border ${ThemeColorsBorder.neutralMid} rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-zinc-400 focus:border-zinc-400`}
                            value={organizationFilter}
                            onChange={(e) => setOrganizationFilter(e.target.value as OrganizationFilter)}
                        >
                            <option value={OrganizationFilter.RELEVANT}>Relevant Only (Tagged Documents)</option>
                            <option value={OrganizationFilter.ALL}>All Documents (Organized in Folders)</option>
                        </select>
                    </div>

                    <div>
                        <label className={`block text-sm font-medium ${TextColors.mid} mb-2`}>
                            Tags to Include *
                            {selectedTags.length > 0 && (
                                <span className={`text-xs ${TextColors.mid} font-normal ml-2`}>
                                    {selectedTags.length} selected
                                </span>
                            )}
                        </label>

                        {/* Selected tags as pills */}
                        {selectedTags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-3">
                                {selectedTags.map(id => {
                                    const tag = tags.find(t => t.id === id);
                                    if (!tag) return null;
                                    return (
                                        <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-zinc-100 text-zinc-700 border border-zinc-200">
                                            <span className="truncate max-w-[140px]">{tag.tag_name}</span>
                                            <button type="button" onClick={() => toggleTag(id)} className="p-0.5 hover:bg-zinc-200 rounded-full">
                                                <FiX className="w-3 h-3"/>
                                            </button>
                                        </span>
                                    );
                                })}
                                {selectedTags.length > 1 && (
                                    <button type="button" onClick={() => setSelectedTags([])} className="text-xs text-zinc-500 hover:text-red-500 px-1">
                                        Clear all
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Global search */}
                        <div className="flex items-center gap-2 px-3 py-2 mb-3 border border-zinc-200 rounded-lg bg-white">
                            <FiSearch className="w-3.5 h-3.5 text-zinc-400"/>
                            <input
                                type="text"
                                placeholder="Search all tags..."
                                value={tagSearch}
                                onChange={e => setTagSearch(e.target.value)}
                                className="flex-1 bg-transparent outline-none text-sm text-zinc-700 placeholder-zinc-400"
                            />
                            {tagSearch && (
                                <button type="button" onClick={() => setTagSearch('')} className="text-zinc-400 hover:text-zinc-600">
                                    <FiX className="w-3.5 h-3.5"/>
                                </button>
                            )}
                        </div>

                        {/* Organized sections */}
                        <div className="space-y-3">
                            <TagSection
                                title="Document Requests" icon={<FiFileText className="w-3.5 h-3.5"/>}
                                tags={tagGroups.requests} selectedTags={selectedTags} toggleTag={toggleTag}
                            />
                            <TagSection
                                title="Codes" icon={<FiTag className="w-3.5 h-3.5"/>}
                                tags={tagGroups.codes} selectedTags={selectedTags} toggleTag={toggleTag}
                            />
                            {tagGroups.transcript.length > 0 && (
                                <TagSection
                                    title="Testimony" icon={<FiTag className="w-3.5 h-3.5"/>}
                                    tags={tagGroups.transcript} selectedTags={selectedTags} toggleTag={toggleTag}
                                />
                            )}
                            {ENABLE_ENTITY_TAGS && tagGroups.entities.length > 0 && (
                                <TagSection
                                    title="Entities" icon={<PiBuildings className="w-3.5 h-3.5"/>}
                                    tags={tagGroups.entities} selectedTags={selectedTags} toggleTag={toggleTag}
                                />
                            )}
                        </div>
                    </div>

                    <button
                        type="submit"
                        className={`w-full px-4 py-2 ${ButtonColors.dark} ${ButtonColors.hoverDark} text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2`}
                        disabled={loading}
                    >
                        <FiCheck/>
                        Create Production
                    </button>
                </form>
            )}

            {/* Productions List */}
            <div className="space-y-3">
                {initialLoading ? (
                    <div className="flex justify-center py-6">
                        <Loader text="Loading productions…"/>
                    </div>
                ) : productions.length === 0 ? (
                    <div className={`text-center py-8 ${TextColors.mid} text-sm`}>
                        No productions created yet. Click *New* to create one.
                    </div>
                ) : (
                    productions.map((production) => (
                        <div
                            key={production.id}
                            className={`border ${ThemeColorsBorder.neutralMid} rounded-lg p-4 ${ThemeColors.white} hover:shadow-md transition-shadow`}
                        >
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                    <h4 className={`font-medium ${TextColors.dark}`}>{production.name}</h4>
                                    {production.description && (
                                        <p className={`text-sm ${TextColors.mid} mt-1`}>{production.description}</p>
                                    )}
                                </div>
                            </div>

                            <div className={`flex flex-wrap gap-2 text-xs ${TextColors.mid} mb-3`}>
                                <span className={`px-2 py-1 ${ThemeColors.neutralMid} rounded`}>
                                    Prefix: {production.bate_prefix}
                                </span>
                                <span className={`px-2 py-1 ${ThemeColors.neutralMid} rounded`}>
                                    {BATE_STAMP_LABELS[production.bate_stamp_location]}
                                </span>
                                {production.exported_at && (
                                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                                        Exported {new Date(production.exported_at).toLocaleDateString()}
                                    </span>
                                )}
                            </div>

                            {/* Show progress bar if there's an active job, otherwise show action buttons */}
                            {(() => {
                                const activeJob = getActiveJobForProduction(production);
                                if (activeJob) {
                                    return (
                                        <div className={`${ThemeColors.neutralLight} rounded-lg p-2`}>
                                            <JobProgressList jobs={[activeJob]}/>
                                        </div>
                                    );
                                }

                                return (
                                    <div className="flex gap-2">
                                        {production.exported_at ? (
                                            <>
                                                <button
                                                    onClick={() => handleDownloadProduction(production.id)}
                                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                                                    disabled={loading}
                                                >
                                                    <FiDownload/>
                                                    Download
                                                </button>
                                                <button
                                                    onClick={() => handleExportProduction(production.id)}
                                                    className={`px-3 py-2 ${ButtonColors.dark} ${ButtonColors.hoverDark} text-white rounded-lg disabled:opacity-50 text-sm`}
                                                    disabled={loading}
                                                    title="Re-export production"
                                                >
                                                    <FiEdit2/>
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                onClick={() => handleExportProduction(production.id)}
                                                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 ${ButtonColors.dark} ${ButtonColors.hoverDark} text-white rounded-lg disabled:opacity-50 text-sm`}
                                                disabled={loading}
                                            >
                                                <FiDownload/>
                                                Start Export
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDeleteProduction(production.id, production.name)}
                                            className={`px-3 py-2 border ${ThemeColorsBorder.error} text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50`}
                                            disabled={loading}
                                        >
                                            <FiTrash2/>
                                        </button>
                                    </div>
                                );
                            })()}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default PaletteProduction;
